import { useState, useEffect, useCallback } from 'react'
import { whatsappService, WhatsAppContact, WhatsAppChat, SendMessageData } from '../services/whatsappService'
import toast from 'react-hot-toast'

export const useWhatsApp = (sessionId: number) => {
  const [status, setStatus] = useState<string>('disconnected')
  const [isConnected, setIsConnected] = useState<boolean>(false)
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [contacts, setContacts] = useState<WhatsAppContact[]>([])
  const [chats, setChats] = useState<WhatsAppChat[]>([])
  const [conversations, setConversations] = useState<WhatsAppChat[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Verificar status da conexão
  const checkStatus = useCallback(async () => {
    if (!sessionId || sessionId === 0) {
      return
    }
    
    try {
      setIsLoading(true)
      const result = await whatsappService.getStatus(sessionId)
      
      if (result.success) {
        setStatus(result.status || 'disconnected')
        setIsConnected(result.isConnected || false)
        setQrCode(result.qrCode || null)
        setError(null)
      } else {
        setError(result.message || 'Erro ao verificar status')
      }
    } catch (error) {
      setError('Erro ao verificar status da conexão')
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  // Conectar
  const connect = useCallback(async () => {
    if (!sessionId || sessionId === 0) {
      toast.error('ID do bot inválido')
      return
    }
    
    try {
      setIsLoading(true)
      const result = await whatsappService.connect(sessionId)
      
      if (result.success) {
        toast.success('Conexão iniciada! Aguarde o QR Code...')
        // Verificar status após conectar
        setTimeout(checkStatus, 2000)
      } else {
        toast.error(result.message || 'Erro ao conectar')
        setError(result.message || 'Erro ao conectar')
      }
    } catch (error) {
      toast.error('Erro ao conectar')
      setError('Erro ao conectar')
    } finally {
      setIsLoading(false)
    }
  }, [sessionId, checkStatus])

  // Desconectar
  const disconnect = useCallback(async () => {
    if (!sessionId || sessionId === 0) {
      toast.error('ID do bot inválido')
      return
    }
    
    try {
      setIsLoading(true)
      const result = await whatsappService.disconnect(sessionId)
      
      if (result.success) {
        toast.success('Desconectado com sucesso')
        setStatus('disconnected')
        setIsConnected(false)
        setQrCode(null)
        setError(null)
      } else {
        toast.error(result.message || 'Erro ao desconectar')
        setError(result.message || 'Erro ao desconectar')
      }
    } catch (error) {
      toast.error('Erro ao desconectar')
      setError('Erro ao desconectar')
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  // Obter contatos
  const loadContacts = useCallback(async () => {
    try {
      setIsLoading(true)
      const result = await whatsappService.getContacts(sessionId)
      
      if (result.success && result.contacts) {
        setContacts(result.contacts)
        setError(null)
      } else {
        setError(result.message || 'Erro ao carregar contatos')
      }
    } catch (error) {
      setError('Erro ao carregar contatos')
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  // Obter chats
  const loadChats = useCallback(async () => {
    try {
      setIsLoading(true)
      const result = await whatsappService.getChats(sessionId)
      
      if (result.success && result.chats) {
        setChats(result.chats)
        setError(null)
      } else {
        setError(result.message || 'Erro ao carregar chats')
      }
    } catch (error) {
      setError('Erro ao carregar chats')
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  // Obter conversas
  const loadConversations = useCallback(async (page = 1, limit = 20) => {
    try {
      setIsLoading(true)
      const result = await whatsappService.getConversations(sessionId, page, limit)
      
      if (result.success && result.conversations) {
        setConversations(result.conversations)
        setError(null)
        return result.pagination
      } else {
        setError(result.message || 'Erro ao carregar conversas')
        return null
      }
    } catch (error) {
      setError('Erro ao carregar conversas')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  // Enviar mensagem
  const sendMessage = useCallback(async (data: SendMessageData) => {
    try {
      setIsLoading(true)
      const result = await whatsappService.sendMessage(sessionId, data)
      
      if (result.success) {
        toast.success('Mensagem enviada com sucesso!')
        return { success: true, messageId: result.messageId }
      } else {
        toast.error(result.message || 'Erro ao enviar mensagem')
        return { success: false, error: result.message }
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem')
      return { success: false, error: 'Erro ao enviar mensagem' }
    } finally {
      setIsLoading(false)
    }
  }, [sessionId])

  // Verificar status periodicamente quando conectando
  useEffect(() => {
    let interval: number | null = null

    if (status === 'authenticating') {
      interval = window.setInterval(checkStatus, 5000) // Verificar a cada 5 segundos
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [status, checkStatus])

  // Verificar status inicial
  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  return {
    // Estado
    status,
    isConnected,
    qrCode,
    contacts,
    chats,
    conversations,
    isLoading,
    error,

    // Ações
    connect,
    disconnect,
    checkStatus,
    loadContacts,
    loadChats,
    loadConversations,
    sendMessage
  }
}
